import React, { useState } from 'react';
import { Undo2, Loader2 } from 'lucide-react';
import { TaskStatus, TASK_STATUS_LABELS, TASK_STATUS_COLORS } from '../../../types/annotation';

interface AdminRescuePanelProps {
  currentStatus: TaskStatus;
  latestReview?: { status: string; comments?: string; reviewerRole?: string };
  onReopen: (
    target: 'submitted' | 'revision_needed' | 'approved',
    comments: string,
  ) => Promise<void>;
}

type ReopenTarget = 'submitted' | 'revision_needed' | 'approved';

interface TargetOption {
  value: ReopenTarget;
  label: string;
  description: string;
}

const TARGETS_FROM_REJECTED: TargetOption[] = [
  { value: 'submitted',       label: 'Reopen to Supervisor',  description: 'Back into the supervisor review queue.' },
  { value: 'revision_needed', label: 'Reopen to Transcriber', description: 'Back to the transcriber as revision needed.' },
  { value: 'approved',        label: 'Reopen to Sub-Admin',   description: 'Treat as supervisor-approved; appears in sub-admin final-review queue.' },
];

const TARGETS_FROM_FINAL_REJECTED: TargetOption[] = [
  { value: 'submitted', label: 'Reopen to Supervisor', description: 'Back into the supervisor review queue.' },
  { value: 'approved',  label: 'Reopen to Sub-Admin',  description: 'Back into the sub-admin final-review queue.' },
];

const AdminRescuePanel: React.FC<AdminRescuePanelProps> = ({
  currentStatus,
  latestReview,
  onReopen,
}) => {
  const [target, setTarget] = useState<ReopenTarget | ''>('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (currentStatus !== 'rejected' && currentStatus !== 'final_rejected') return null;

  const targets =
    currentStatus === 'rejected' ? TARGETS_FROM_REJECTED : TARGETS_FROM_FINAL_REJECTED;

  const handleSubmit = async () => {
    if (!target) return;
    setSubmitError(null);
    if (!comments.trim()) {
      setSubmitError('Please add a comment explaining why this task is being reopened.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onReopen(target, comments);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to reopen task.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white border border-amber-300 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
          <Undo2 size={14} />
          Admin Rescue
        </h3>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${TASK_STATUS_COLORS[currentStatus]}`}
        >
          {TASK_STATUS_LABELS[currentStatus]}
        </span>
      </div>

      <div className="px-4 py-4 space-y-4">
        <p className="text-xs text-gray-600">
          This task was {currentStatus === 'rejected' ? 'rejected by a supervisor' : 'final-rejected by a sub-admin'}
          {latestReview?.comments ? `: "${latestReview.comments}"` : '.'}
          {' '}As an admin you can rescue it back into one of the review queues.
        </p>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Reopen to
          </label>
          <div className="space-y-1.5">
            {targets.map((t) => (
              <label
                key={t.value}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  target === t.value
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="rescue-target"
                  value={t.value}
                  checked={target === t.value}
                  onChange={() => setTarget(t.value)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{t.label}</div>
                  <div className="text-xs text-gray-500">{t.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Comment <span className="text-red-500">*</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={isSubmitting}
            rows={3}
            placeholder="Required — explain why you're reopening this task."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none disabled:opacity-50"
          />
        </div>

        {submitError && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {submitError}
          </p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !target}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg font-medium bg-amber-600 text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
          Reopen Task
        </button>
      </div>
    </div>
  );
};

export default AdminRescuePanel;
